// Sidebar's top-left workspace/scope switcher dropdown — extracted from
// Sidebar.tsx during the P5 pass-2 split. Render is prop-driven; the only
// local state is the self-contained community-directory dialog toggle (Phase
// 4), kept here so the entry point doesn't thread through Sidebar's plumbing.

import { Suspense, lazy, useState } from "react";
import { CaretDown, House, GlobeHemisphereWest } from "@/components/ui/icons";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { AnimatedImage } from "@/components/ui/animated-image";
import { Button } from "@/components/ui/button";
import { WorkspaceIcon } from "@/components/ui/workspace-icon";
import { cn } from "@/lib/utils";

const CommunityDirectoryDialog = lazy(() =>
  import("@/components/dialogs/CommunityDirectoryDialog").then((m) => ({
    default: m.CommunityDirectoryDialog,
  })),
);

type WorkspaceLike = {
  id: string;
  name?: string | null;
  slug?: string | null;
  logo_url?: string | null;
};

type CurrentUserLike = {
  avatar_url?: string | null;
};

interface WorkspaceUnreadStats {
  unread: number;
  mentions: number;
}

interface WorkspaceSwitcherProps {
  scope: string;
  selectedWorkspace: string | null;
  onScopeChange: (scope: string) => void;
  currentWorkspace: WorkspaceLike | undefined;
  currentUserData: CurrentUserLike | null | undefined;
  userDisplayName?: string | null;
  // Caller passes the workspace list verbatim — same loose `any[]` shape
  // SidebarProps.workspaceList uses.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  workspaceList: any[];
  // Per-workspace unread + mention totals; used to render an unread dot
  // (or red mention pill) next to each workspace in the dropdown so users
  // can see at a glance which workspaces have activity without switching
  // scope. Optional so callers that don't have the data yet still render.
  workspaceUnreads?: ReadonlyMap<string, WorkspaceUnreadStats>;
}

export function WorkspaceSwitcher({
  scope,
  selectedWorkspace,
  onScopeChange,
  currentWorkspace,
  currentUserData,
  userDisplayName,
  workspaceList,
  workspaceUnreads,
}: WorkspaceSwitcherProps) {
  const [showDirectory, setShowDirectory] = useState(false);
  const isHomeScope = scope === "home";
  const isWorkspaceScope = !isHomeScope;
  const hasWorkspaceLogo = Boolean(currentWorkspace?.logo_url?.trim());
  const shouldUseAnoDefaultLogo =
    !hasWorkspaceLogo &&
    (currentWorkspace?.slug === "ano" ||
      currentWorkspace?.name?.trim().toLowerCase() === "ano");

  return (
    <div className="min-w-0 max-w-full overflow-hidden">
      <Suspense fallback={null}>
        {showDirectory && (
          <CommunityDirectoryDialog
            open={showDirectory}
            onOpenChange={setShowDirectory}
          />
        )}
      </Suspense>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          {/* `min-w-0` + the truncated name's natural flex shrink let this
              button collapse as far as the available row width allows,
              so the right-side action icons (new-message + settings gear)
              always stay visible regardless of how long the workspace
              name is (#1797). The image and caret carry `shrink-0` so
              only the name text gives way. */}
          <Button
            type="button"
            variant="ghost"
            className="h-auto max-w-full min-w-0 cursor-pointer gap-2 px-2 py-1.5 [-webkit-app-region:no-drag]"
          >
            {isWorkspaceScope && (currentWorkspace || selectedWorkspace) ? (
              <>
                {hasWorkspaceLogo || shouldUseAnoDefaultLogo ? (
                  <div className="h-7 w-7 shrink-0 rounded bg-sidebar flex items-center justify-center overflow-hidden">
                    <AnimatedImage
                      src={
                        hasWorkspaceLogo
                          ? (currentWorkspace?.logo_url ?? undefined)
                          : "/workspace-logo.webp"
                      }
                      alt={`${currentWorkspace?.name || "Workspace"} logo`}
                      className="h-7 w-7"
                    />
                  </div>
                ) : (
                  <WorkspaceIcon
                    name={currentWorkspace?.name ?? ""}
                    logoUrl={currentWorkspace?.logo_url}
                    slug={currentWorkspace?.slug}
                    size="md"
                    className="shrink-0"
                  />
                )}
                <span className="min-w-0 font-semibold text-sm truncate">
                  {currentWorkspace?.name || "Loading..."}
                </span>
              </>
            ) : (
              <>
                {currentUserData?.avatar_url ? (
                  <div className="h-7 w-7 rounded-full overflow-hidden shrink-0">
                    <AnimatedImage
                      src={currentUserData.avatar_url}
                      alt={userDisplayName || "User"}
                      className="h-7 w-7 object-cover"
                    />
                  </div>
                ) : (
                  <div className="h-7 w-7 rounded-full bg-foreground/20 flex items-center justify-center shrink-0">
                    <House
                      size={14}
                      weight="Bold"
                      className="text-foreground"
                    />
                  </div>
                )}
                <span className="font-semibold text-sm">Home</span>
              </>
            )}
            <CaretDown size={12} className="shrink-0 text-muted-foreground" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="min-w-[200px]">
          {/* Home */}
          <DropdownMenuItem
            onClick={() => onScopeChange("home")}
            className={cn(
              "cursor-pointer",
              isHomeScope && "bg-foreground/[0.06]",
            )}
          >
            {currentUserData?.avatar_url ? (
              <div className="h-5 w-5 rounded-full overflow-hidden mr-2 shrink-0">
                <AnimatedImage
                  src={currentUserData.avatar_url}
                  alt=""
                  className="h-5 w-5 object-cover"
                />
              </div>
            ) : (
              <House size={16} className="mr-2 text-muted-foreground" />
            )}
            Home
          </DropdownMenuItem>
          {workspaceList.length > 0 && <DropdownMenuSeparator />}
          {/* Workspaces (alphabetical) */}
          {[...workspaceList]
            .sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""))
            .map((w) => {
              const stats = workspaceUnreads?.get(w.id);
              const mentions = stats?.mentions ?? 0;
              const hasUnread = (stats?.unread ?? 0) > 0;
              return (
                <DropdownMenuItem
                  key={w.id}
                  onClick={() => onScopeChange(w.id)}
                  className={cn(
                    "cursor-pointer",
                    scope === w.id && "bg-foreground/[0.06]",
                  )}
                >
                  <WorkspaceIcon
                    name={w.name}
                    logoUrl={w.logo_url}
                    slug={w.slug}
                    size="sm"
                    className="mr-2"
                  />
                  <span
                    className={cn(
                      "min-w-0 flex-1 truncate",
                      hasUnread && "font-semibold",
                    )}
                  >
                    {w.name}
                  </span>
                  {mentions > 0 ? (
                    <span className="ml-auto bg-destructive text-primary-foreground text-[10px] px-1.5 py-0.5 rounded-full font-semibold shrink-0 min-w-[18px] text-center">
                      @{mentions}
                    </span>
                  ) : hasUnread ? (
                    <span className="ml-auto h-2 w-2 rounded-full bg-foreground shrink-0" />
                  ) : null}
                </DropdownMenuItem>
              );
            })}
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={() => setShowDirectory(true)}
            className="cursor-pointer"
          >
            <GlobeHemisphereWest
              size={16}
              className="mr-2 text-muted-foreground"
            />
            Discover communities
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
